import DEFAULT_I18N from "~/i18n"
import { defaultCtx } from "~/lib/AjaxCtx"
import { notify } from "~/lib/bugsnag"
import { ucfirst } from "~/lib/utils"

import MessageService from "./MessageService"

function makeDefaultFolder() {
    return {
        messages: [],
        unreadMessages: 0,
        status: "ready",
        cursor: null,
        end: false,
        lastPollingTime: new Date().toISOString(),
        query: "",
        filter: "date", // date | unread
    }
}

export function makeDefaultState() {
    return {
        inbox: makeDefaultFolder(),
        popup: makeDefaultFolder(),
        sent: makeDefaultFolder(),
        trash: makeDefaultFolder(),
        newMessage: makeEmptyMessage(),
    }
}

export function makeEmptyMessage() {
    return {
        to: {
            includedUsers: [],
            excludedUsers: [],
            filters: [],
        },
        subject: "",
        body: "",
        attachments: [],
        repliedId: null,
        forwardedId: null,
        isPrivate: true,
        usersCount: 0,
    }
}

export const getters = {
    getMessage:
        state =>
        ({ messageUserId, folder }) => {
            if (!(folder in state)) {
                throw new Error(`${folder} folder doesn't exist`)
            }
            return state[folder].messages.find(
                m => m.messageUserId == messageUserId
            )
        },
}

export const mutations = {
    setMessage(state, { message, folder }) {
        if (!folder) {
            folder = message.folder
        } else {
            message.folder = folder // update folder value if passed as a parameter
        }

        if (!(folder in state)) {
            throw new Error(`${folder} folder is not defined`)
        }

        const messageUserId = message.messageUserId
        const messageIndex = state[folder].messages.findIndex(
            m => m.messageUserId == messageUserId
        )

        if (messageIndex !== -1) {
            const currentMessage = state[folder].messages[messageIndex]
            Object.keys(message).forEach(key => {
                currentMessage[key] = message[key]
            })
            return
        }

        const messageDateIndex = state[folder].messages.findIndex(
            m => m.date < message.date
        )

        if (messageDateIndex !== -1) {
            state[folder].messages.splice(messageDateIndex, 0, message)
            return
        }

        state[folder].messages.push(message)
    },

    setNewMessage(state, message) {
        Object.assign(state.newMessage, message)
    },

    setFolder(state, { folder, data }) {
        if (!(folder in state)) {
            throw new Error(`${folder} folder is not defined`)
        }

        Object.assign(state[folder], data)
    },

    clearMessage(state, { messageUserId, folder }) {
        if (!(folder in state)) {
            throw new Error(`${folder} folder is not defined`)
        }

        const messageIndex = state[folder].messages.findIndex(
            m => m.messageUserId == messageUserId
        )
        if (messageIndex === -1) {
            throw new Error(`Message ${messageUserId} can't be found`)
        }

        state[folder].messages.splice(messageIndex, 1)
    },

    setUnreadMessages(state, unreadMessages) {
        state.inbox.unreadMessages = unreadMessages
    },

    clearModule(state) {
        state.inbox = makeDefaultFolder()
        state.popup = makeDefaultFolder()
        state.sent = makeDefaultFolder()
        state.trash = makeDefaultFolder()
        state.newMessage = makeEmptyMessage()
    },

    clearNewMessage(state) {
        state.newMessage = makeEmptyMessage()
    },

    clearFolder(state, folder) {
        const folderData = state[folder]

        folderData.messages = []
        folderData.cursor = null
        folderData.end = false
    },
}

export function makeActions(service, i18n) {
    i18n = i18n || DEFAULT_I18N.global

    return {
        /**
         * This action deletes or recovers a message.
         * Messages stored in trash are permanently deleted when deleted and moved to their original folder when recovered
         * Only messages stored in trash are meant to be recovered.
         *
         * @param {number} messageUserId
         * @param {string} folder (current folder)
         * @param {boolean} isDeleted - true: deletes the message, false: recovers the message to the original folder
         */
        async deleteMessage(
            { commit, state, dispatch },
            { messageUserId, folder, isDeleted }
        ) {
            const message = state[folder].messages.find(
                m => m.messageUserId == messageUserId
            )

            try {
                const data = await service.deleteMessage({
                    messageUserId,
                    isDeleted,
                })
                const destinationFolder = data.folder

                dispatch("updateUnreadMessages")

                if (destinationFolder) {
                    commit("setMessage", {
                        message,
                        folder: destinationFolder,
                    })
                }

                if (isDeleted) {
                    dispatch(
                        "pushNotification",
                        {
                            title: ucfirst(
                                i18n.t("messages.messageWasDeleted")
                            ),
                        },
                        { root: true }
                    )
                }

                commit("clearMessage", { messageUserId, folder })
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        async getMessageDetails({ commit, dispatch }, { messageUserId }) {
            try {
                const message = await service.getMessageDetails({
                    messageUserId,
                })
                commit("setMessage", { message })
            } catch (error) {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                )
                    return
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        async getNewMessages({ state, commit }, { folder }) {
            commit("setFolder", {
                folder,
                data: { status: "automatic-refreshing" },
            })
            const { lastPollingTime } = state[folder]

            try {
                const { data: messages } = await service.getMessages({
                    folder,
                    cursor: null,
                    query: state[folder].query,
                    unread: state[folder].filter == "unread",
                    date: lastPollingTime,
                })

                for (const message of messages) {
                    commit("setMessage", { message, folder })
                }
            } catch (error) {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                )
                    return
                notify(error)
            } finally {
                commit("setFolder", {
                    folder,
                    data: {
                        status: "ready",
                        lastPollingTime: new Date().toISOString(),
                    },
                })
            }
        },

        async loadMore({ state, commit }, { folder }) {
            commit("setFolder", { folder, data: { status: "refreshing" } })

            try {
                const { data: messages, meta } = await service.getMessages({
                    folder,
                    cursor: state[folder].cursor,
                    query: state[folder].query,
                    unread: state[folder].filter == "unread",
                })

                commit("setFolder", {
                    folder,
                    data: {
                        cursor: meta.cursor.next,
                        end: !meta.cursor.next,
                    },
                })

                for (const message of messages) {
                    commit("setMessage", { message, folder })
                }
            } catch (error) {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                )
                    return
                notify(error)
            } finally {
                commit("setFolder", { folder, data: { status: "ready" } })
            }
        },

        async refreshFolder({ state, commit, dispatch }, { folder = "inbox" }) {
            // skip refresh if it's already ongoing
            if (state[folder].status !== "ready") {
                notify("You are refreshing the data beyond your means")
                return
            }

            commit("setFolder", { folder, data: { status: "refreshing" } })
            commit("clearFolder", folder)

            try {
                const limit = folder === "popup" ? 4 : null
                const { data: messages, meta } = await service.getMessages({
                    folder: folder === "popup" ? "inbox" : folder,
                    cursor: state[folder].cursor,
                    query: state[folder].query,
                    unread: state[folder].filter == "unread",
                    limit,
                })

                commit("setFolder", {
                    folder,
                    data: {
                        cursor: meta.cursor.next,
                        end: !meta.cursor.next,
                    },
                })

                for (const message of messages) {
                    commit("setMessage", { message, folder })
                }

                if (messages.length > 0) {
                    dispatch("getMessageDetails", messages[0])
                }
            } catch (error) {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                )
                    return
                notify(error)
            } finally {
                commit("setFolder", { folder, data: { status: "ready" } })
            }
        },

        async sendNewMessage({ commit, dispatch, state, rootGetters }) {
            const sender = rootGetters.currentUser
            const newMessage = state.newMessage

            const message = await service.newMessage({
                ...newMessage,
                sender,
            })

            commit("setMessage", {
                message,
                folder: "sent",
            })

            dispatch(
                "pushNotification",
                {
                    title: ucfirst(i18n.t("messages.messageWasSent")),
                    type: "success",
                },
                { root: true }
            )

            return message
        },

        setFolder({ commit }, { folder, data }) {
            commit("setFolder", { folder, data })
        },

        async setReadStatus({ commit, dispatch }, { messageUserId, isRead }) {
            try {
                const { unreadMessages } = await service.setReadStatus({
                    messageUserId,
                    isRead,
                })

                commit("setUnreadMessages", unreadMessages)

                dispatch("getMessageDetails", {
                    messageUserId,
                })
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        title: error,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        async updateFolder({ dispatch }, folder) {
            if (["inbox", "popup"].includes(folder)) {
                dispatch("updateUnreadMessages")
            }

            await dispatch("refreshFolder", { folder })
        },

        async updateUnreadMessages({ commit, dispatch }) {
            try {
                const unreadMessages = await service.getUnreadMessages()
                commit("setUnreadMessages", unreadMessages)
            } catch (error) {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                )
                    return
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        updateNewMessage({ commit }, newMessage) {
            commit("setNewMessage", newMessage)
        },
    }
}

export default {
    namespaced: true,
    state: makeDefaultState(),
    getters,
    mutations,
    actions: makeActions(MessageService(defaultCtx)),
}
